import React, { useState, useEffect } from 'react';
import { NavLink, Link } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Tabs,
  Tab,
  MenuList,
  Divider,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Drawer,
  ListItemSecondaryAction,
  Badge,
  FormControlLabel,
  FormGroup,
  Switch,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import MenuIcon from '@material-ui/icons/Menu';
import CloseIcon from '@material-ui/icons/Close';
import LibraryIcon from '@material-ui/icons/Book';
import ReportsIcon from '@material-ui/icons/Assignment';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import SupervisedUserCircleIcon from '@material-ui/icons/SupervisedUserCircle';
import SettingsIcon from '@material-ui/icons/Settings';
import NewReleasesIcon from '@material-ui/icons/NewReleases';
import { TasksIcon } from 'global/components/PerformIcons';
import NavRealm from '../NavRealm';
import logo from 'assets/logo.svg';
import ibacoslogo from 'assets/ibacos_innovation.svg';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import useStyles from './NavBar.styles';
import { useSelector } from 'react-redux';
import { get, size } from 'global/lib/lodash';
import { userHasPermissions } from 'redux/actions/userLogin';
import useNavSettings from 'global/hooks/useNavSettings';
import Image from 'global/components/image';
import { stopProp } from 'global/utils';
import { useView } from 'global/hooks/useView';
import clsx from 'clsx';

export default function NavBar(props) {
  const classes = useStyles();
  const { pageValue } = props;
  const [drawerOpen, changeDrawerOpen] = useState(false);
  const { nav } = useNavSettings();
  const pages = [
    {
      label: (
        <>
          <TasksIcon className={classes.tabIcon} />
          tasks
        </>
      ),
      page: 'tasks',
      canDisplay: () => get(nav, 'tasks'),
    },
    {
      label: (
        <>
          <ReportsIcon className={classes.tabIcon} />
          assessments
        </>
      ),
      page: 'reports',
      canDisplay: () => get(nav, 'assessments'),
    },
    {
      label: (
        <>
          <LibraryIcon className={classes.tabIcon} />
          library
        </>
      ),
      page: 'library',
      canDisplay: () => get(nav, 'library'),
    },
  ];

  return (
    <>
      <Drawer open={drawerOpen} onClose={() => changeDrawerOpen(false)}>
        <Link to="/home" className={classes.headerlogoWrapper} onClick={() => changeDrawerOpen(false)}>
          <SidebarLogo />
        </Link>
        <Divider />
        <div tabIndex={0} role="button" onKeyDown={() => changeDrawerOpen(false)}>
          <SideList onClick={() => changeDrawerOpen(false)} />
        </div>
        <img src={logo} className={classes.performlogo} alt="logo" />
        <div className={classes.copyright}>
          <p>&copy; {new Date().getFullYear()} IBACOS Perform</p>
          <Link component="a" to="/changelog" className={classes.newlink}>
            <NewReleasesIcon className={classes.tabIcon} />
            <span>What's New</span>
          </Link>
        </div>
      </Drawer>
      <AppBar position="static" className={clsx(classes.root, props.hide ? classes.hidden : undefined)}>
        <Toolbar>
          <IconButton onClick={() => changeDrawerOpen(true)} className={props.sidebar ? classes.menuButtonSidebar : classes.menuButton} color="inherit" aria-label="Menu">
            <MenuIcon />
          </IconButton>
          <Tabs className={classes.tabs} value={pageValue} variant="standard">
            {pages
              .filter((f) => f.canDisplay())
              .map((item, index) => (
                <Tab classes={{ wrapper: classes.tab }} component={Link} to={`/${item.page}`} label={item.label} value={item.page} key={index} />
              ))}
          </Tabs>
          <Typography variant="h6" color="inherit" className={classes.title}>
            {props.pageTitle}
          </Typography>
          <div className={classes.grow} />
          <NavRealm />
        </Toolbar>
      </AppBar>
    </>
  );
}

export function NavBarContent(props) {
  const classes = useStyles();
  return (
    <div className={classes.root}>
      <AppBar position="static" className={classes.contentRoot}>
        <Toolbar>
          <IconButton
            component={Link}
            to={typeof props.link === 'string' ? `/${props.link}` : props.link}
            className={classes.menuButton}
            color="inherit"
            aria-label="Menu"
            {...(props.closeProps || {})}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" color="inherit">
            {props.pageTitle}
          </Typography>
          <div className={classes.grow} />
          {props.space && <div className={classes.taskButton} />}
        </Toolbar>
      </AppBar>
    </div>
  );
}

const TOPMENU = [
  { label: 'tasks', page: 'tasks', icon: <TasksIcon />, canDisplay: (p) => get(p, 'nav.tasks') },
  { label: 'assessments', page: 'reports', icon: <ReportsIcon />, canDisplay: (p) => get(p, 'nav.assessments') },
  { label: 'library', page: 'library', icon: <LibraryIcon />, canDisplay: (p) => get(p, 'nav.library') },
];

const MENUITEMS = [
  { label: 'profile', page: 'profile', icon: <AccountCircleIcon /> },
  {
    label: 'alerts',
    page: 'alerts',
    icon: <NotificationsActiveIcon />,
    useSecondaryAction: (p) => {
      const view = useSelector((state) => get(state, 'view'));
      const amt = size(get(view, 'alerts.new'));
      if (amt > 0) {
        return <Badge badgeContent={amt >= 25 ? '25+' : amt} color={'primary'} />;
      }
    },
  },
  { label: 'manage users', page: 'users/manage', icon: <SupervisedUserCircleIcon />, disabled: () => !userHasPermissions(['manageUsers']) },
  { label: 'account settings', page: 'account/settings', icon: <SettingsIcon />, disabled: () => !userHasPermissions(['manageAccount']) },
];

function SideList(props) {
  const classes = useStyles();
  const { nav } = useNavSettings();
  const { view, toggleTheme } = useView();

  const toggleDark = (e) => {
    stopProp(e);
    toggleTheme();
  };

  return (
    <div onClick={props.onClick} style={{ width: 250 }}>
      <MenuList>
        {TOPMENU.filter((tm) => tm.canDisplay({ nav })).map((text) => (
          <MenuItem component={NavLink} activeClassName={classes.active} to={`/${text.page}`} key={text.label}>
            <ListItemIcon>{text.icon}</ListItemIcon>
            <ListItemText className={classes.menuTitle} primary={text.label} />
          </MenuItem>
        ))}
      </MenuList>
      <Divider />
      <MenuList>
        {MENUITEMS.map((text) => (
          <MenuItem component={NavLink} activeClassName={classes.active} to={`/${text.page}`} key={text.page} disabled={get(text, 'disabled', () => false)()}>
            <ListItemIcon>{text.icon}</ListItemIcon>
            <ListItemText className={classes.menuTitle} primary={text.label} />
            {text.useSecondaryAction && <ListItemSecondaryAction>{text.useSecondaryAction(props)}</ListItemSecondaryAction>}
          </MenuItem>
        ))}
      </MenuList>
      <Divider />
      <MenuItem component={NavLink} activeClassName={classes.active} to="/logout">
        <ListItemIcon>
          <ExitToAppIcon />
        </ListItemIcon>
        <ListItemText className={classes.menuTitle} primary="Logout" />
      </MenuItem>
      <MenuItem onClick={toggleDark}>
        <FormGroup>
          <FormControlLabel control={<Switch checked={get(view, 'style.palette.type', 'light') === 'dark'} />} label="Dark Theme" />
        </FormGroup>
      </MenuItem>
    </div>
  );
}

function SidebarLogo(props) {
  const theme = useTheme();
  const classes = useStyles();
  const [logo, changeLogo] = useState(ibacoslogo);

  useEffect(() => {
    if ('logo' in theme.props) {
      changeLogo(theme.props.logo);
    } else {
      changeLogo(ibacoslogo);
    }
  }, [theme]);

  return <Image src={logo} alt="" className={classes.headerlogo} />;
}
